import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Compass, ChevronLeft } from 'lucide-react';

interface Message {
  role: 'user' | 'ranger';
  text: string;
}

const MAX_MESSAGES_PER_SESSION = 20;

const SYSTEM_PROMPT = `You are Ranger, TruePath's friendly AI guide. You help students and professionals discover their career paths.

PERSONALITY:
- Warm, encouraging, and smart — like a friend who knows a lot about careers
- For high school students: feel like a cool older sibling who's been through it
- For college students: feel like a sharp peer who gives real talk
- For professionals: feel like a knowledgeable colleague who gets it
- Keep responses SHORT (2-4 sentences max unless asked for detail)
- Use simple language. No jargon. No corporate speak.

WHAT YOU CAN DO:
- Answer questions about the assessment ("What does this question mean?")
- Help fill out the assessment if someone is unsure ("I don't know what to pick")
- Discuss results ("Why did you recommend this path?")
- Answer career questions ("Is computer science a good major?")
- Explain TruePath ("What is this? How does it work?")

GUARDRAILS — STRICTLY FOLLOW THESE:
- NEVER give medical, legal, financial, or mental health advice. Say: "That's something to talk about with [a counselor / a professional / a trusted adult]. I can help with career stuff though!"
- NEVER replace school counselors. When asked personal/emotional questions, say: "Your school counselor would be way better at this than me — they actually know you. But I can help you think about [career topic]."
- NEVER discuss inappropriate topics. If someone tries, redirect: "I'm all about helping you find your path! What career stuff can I help with?"
- NEVER share personal opinions on politics, religion, or controversial social topics.
- NEVER pretend to be a human. If asked: "I'm Ranger, TruePath's AI guide. I'm here to help you explore career paths!"
- Keep all responses appropriate for 13+ year olds.
- If you don't know something, say so. Don't make things up.

WHEN HELPING WITH THE ASSESSMENT:
- Ask one question at a time
- Give concrete examples to help them decide
- If they say "I don't know", suggest options based on what they've shared so far`;

const QUICK_PROMPTS = [
  "What is TruePath?",
  "Help me with this question",
  "I don't know what to pick",
  "Tell me about my results",
];

const Ranger = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: 'ranger', text: "Hey! I'm Ranger, your career guide. Need help with the assessment, want to talk about your results, or just curious about career stuff? I'm here." },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text?: string) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;

    if (messageCount >= MAX_MESSAGES_PER_SESSION) {
      setMessages(prev => [...prev, {
        role: 'ranger',
        text: "I've hit my message limit for this session! Refresh the page to chat again.",
      }]);
      return;
    }

    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setLoading(true);
    setMessageCount(c => c + 1);

    try {
      const history = messages.map(m => ({
        role: m.role === 'ranger' ? 'assistant' : 'user',
        content: m.text,
      }));
      history.push({ role: 'user', content: msg });

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ranger-chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({
            messages: history.slice(-10),
            systemPrompt: SYSTEM_PROMPT,
          }),
        }
      );

      if (!response.ok) throw new Error('Failed');

      const data = await response.json();
      setMessages(prev => [...prev, { role: 'ranger', text: data.reply || "Hmm, let me think... Try asking that differently!" }]);
    } catch {
      const lower = msg.toLowerCase();
      let reply = "I'm having trouble connecting right now. Try refreshing!";
      if (lower.includes('help') || lower.includes('what') || lower.includes('how')) {
        reply = "I can help with the assessment, explain your results, or answer career questions. What's on your mind?";
      } else if (lower.includes('hi') || lower.includes('hello') || lower.includes('hey')) {
        reply = "Hey! Ready to explore some career paths? Ask me anything!";
      }
      setMessages(prev => [...prev, { role: 'ranger', text: reply }]);
    }

    setLoading(false);
  };

  return (
    <>
      {/* Tab on right edge — always visible when panel is closed */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 50, opacity: 0 }}
            onClick={() => setOpen(true)}
            className="fixed right-0 top-1/2 -translate-y-1/2 z-50 flex items-center gap-1.5 pr-3 pl-2 py-3 rounded-l-xl bg-primary text-primary-foreground shadow-lg hover:shadow-xl hover:pr-4 transition-all"
            aria-label="Open Ranger, your career guide"
          >
            <ChevronLeft className="w-3 h-3" />
            <span className="text-xs font-semibold" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
              Ranger
            </span>
            <Compass className="w-4 h-4" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Slide-out panel from left */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop on mobile */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden"
            />

            <motion.div
              initial={{ x: 380 }}
              animate={{ x: 0 }}
              exit={{ x: 380 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="fixed right-0 top-0 bottom-0 w-[360px] max-w-[85vw] bg-background border-l border-border shadow-2xl flex flex-col z-50"
            >
              {/* Header */}
              <div className="px-4 py-4 border-b border-border flex items-center justify-between bg-primary/5 shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                    <Compass className="w-5 h-5 text-primary-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground">Ranger</p>
                    <p className="text-[11px] text-muted-foreground">Your career guide</p>
                  </div>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="w-8 h-8 rounded-lg hover:bg-secondary flex items-center justify-center transition-colors"
                  aria-label="Close Ranger"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                {messages.map((msg, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-primary text-primary-foreground rounded-2xl rounded-br-md'
                          : 'bg-secondary text-foreground rounded-2xl rounded-bl-md'
                      }`}
                    >
                      {msg.text}
                    </div>
                  </motion.div>
                ))}

                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-secondary rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex gap-1.5">
                        <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Quick prompts — show only at start */}
              {messages.length <= 2 && (
                <div className="px-4 pb-2 flex flex-wrap gap-2">
                  {QUICK_PROMPTS.map((prompt) => (
                    <button
                      key={prompt}
                      onClick={() => sendMessage(prompt)}
                      className="px-3 py-1.5 rounded-full text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              )}

              {/* Remaining messages */}
              {messageCount > MAX_MESSAGES_PER_SESSION * 0.7 && (
                <div className="px-4 py-1 text-center">
                  <span className="text-[10px] text-muted-foreground">
                    {MAX_MESSAGES_PER_SESSION - messageCount} messages left
                  </span>
                </div>
              )}

              {/* Input */}
              <div className="px-4 py-3 border-t border-border shrink-0">
                <form
                  onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
                  className="flex items-center gap-2"
                >
                  <input
                    type="text"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    placeholder="Ask Ranger anything..."
                    aria-label="Message Ranger"
                    className="flex-1 px-4 py-3 rounded-xl bg-secondary text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-primary/20"
                    disabled={loading || messageCount >= MAX_MESSAGES_PER_SESSION}
                  />
                  <button
                    type="submit"
                    disabled={!input.trim() || loading || messageCount >= MAX_MESSAGES_PER_SESSION}
                    className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center shrink-0 hover:opacity-90 transition-opacity disabled:opacity-40"
                    aria-label="Send message"
                  >
                    <Send className="w-4 h-4 text-primary-foreground" />
                  </button>
                </form>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Ranger;
